import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, ArrowDown, ArrowUp, CheckCircle2, Database, Loader2, PhoneCall, RefreshCw, ShieldCheck, X } from 'lucide-react';
import { cn } from '../lib/utils';
import { Button } from '@/components/ui/Button';

/**
 * CollectionsPage — Stream B "Delinquency & Collections Outreach".
 *
 *   GET  /api/core/sandbox/collections/queue?minDpd&maxDpd&limit           deterministic SQL — the 10–60 DPD book
 *   POST /api/core/sandbox/collections/:accountNumber/outreach-plan        Uptiq Collections agent → Zod → grounding → lint
 *   POST /api/core/sandbox/collections/:accountNumber/outreach-plan/queue  → compliance officer review queue
 *
 * Nothing on this page sends anything to a member. The draft is read-only; the only action is
 * "Send to compliance review", which lands the plan in the staff review queue for a compliance
 * officer. Failures render as failures (an ungrounded or non-compliant plan is a 502, not a plan).
 * Values arrive as strings ('' = null) because the transport casts every column to text.
 */

type Row = Record<string, string>;

interface QueueResponse {
  minDpd: number;
  maxDpd: number;
  limit: number;
  rows: Row[];
  meta: { executionId: string; latencyMs: number; sql: string; cached?: boolean };
}

interface ContactPlanStep {
  channel: 'phone' | 'sms' | 'email' | 'letter' | 'in_person';
  when: string;
  purpose: string;
}

interface OutreachPlan {
  recoverability_score: number;
  drivers: string[];
  contact_plan: ContactPlanStep[];
  /** null on a compliance hold (no outreach recommended) */
  first_message_draft: string | null;
  compliance_notes: string[];
  do_not: string[];
}

interface PlanResponse {
  accountNumber: string;
  memberNumber: string;
  plan: OutreachPlan;
  grounding: { checked: number; amounts: number; dates: number };
  lint: { rulesEvaluated: number; violations: unknown[] };
  data: Record<string, unknown>;
  agent: { executionId: string; latencyMs: number; agentId: string };
  meta: { executionId: string; latencyMs: number; sql: string }[];
}

type SortKey = 'days_past_due' | 'current_principal' | 'amount_past_due' | 'next_payment_date';

function getJwt(): string {
  return localStorage.getItem('crm_dev_token') ?? '';
}

class ApiError extends Error {
  constructor(message: string, public readonly code?: string, public readonly details?: unknown, public readonly status?: number) {
    super(message);
  }
}

/** Queue rows shown per load. The agent transcribes result sets: 25 full rows ≈ 22s, 50 ≈ 33s against a 40s statement timeout; the old 200 never returned. */
const QUEUE_PAGE = 25;

/**
 * One automatic retry when the agent times out (504). The executor hangs single calls intermittently and
 * crm-api caches the deterministic core queries, so the second attempt costs only the missing call. The
 * caller shows the retry; this never loops.
 */
async function withOneRetryOnTimeout<T>(fn: () => Promise<T>, onRetry: () => void): Promise<T> {
  try {
    return await fn();
  } catch (first) {
    if (!(first instanceof ApiError && first.status === 504)) throw first;
    onRetry();
    return await fn();
  }
}

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, { ...init, headers: { Authorization: `Bearer ${getJwt()}`, 'content-type': 'application/json', ...(init?.headers ?? {}) } });
  if (res.status === 503) throw new ApiError('The collections agent is not configured on this environment.', undefined, undefined, 503);
  if (res.status === 504) throw new ApiError('The agent timed out. Try again.', undefined, undefined, 504);
  if (res.status === 401) throw new ApiError('Your session has expired. Sign in again.');
  if (res.status === 403) throw new ApiError('Your role cannot access collections.');
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { message?: string | { code?: string; message?: string; failures?: unknown; violations?: unknown } };
    const m = body.message;
    if (typeof m === 'object' && m !== null) throw new ApiError(m.message ?? `Request failed (HTTP ${res.status}).`, m.code, m.failures ?? m.violations);
    throw new ApiError(typeof m === 'string' ? m : `Request failed (HTTP ${res.status}).`);
  }
  return (await res.json()) as T;
}

function money(v: string | undefined): string {
  if (v === undefined || v === '') return '—';
  const n = Number(v);
  return Number.isNaN(n) ? v : n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}
function date(v: string | undefined): string {
  if (v === undefined || v === '') return '—';
  const d = new Date(v.length === 10 ? `${v}T00:00:00` : v);
  return Number.isNaN(d.getTime()) ? v : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
function text(v: string | undefined): string {
  return v === undefined || v === '' ? '—' : v;
}

function ErrorBox({ error }: { error: ApiError }): React.ReactElement {
  const details = Array.isArray(error.details) ? (error.details as Record<string, string>[]) : [];
  return (
    <div role="alert" className="rounded-lg border border-fail-soft bg-fail-soft px-4 py-3 text-sm text-fail">
      <div className="flex items-start gap-2">
        <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" aria-hidden="true" />
        <div>
          {error.code && <span className="font-mono text-xs bg-fail-soft rounded px-1.5 py-0.5 mr-2">{error.code}</span>}
          <span>{error.message}</span>
        </div>
      </div>
      {details.length > 0 && (
        <ul className="mt-2 ml-6 list-disc text-xs space-y-0.5">
          {details.slice(0, 8).map((d, i) => (
            <li key={i}>{d['rule'] ?? d['kind']}: <span className="font-mono">{d['match'] ?? d['text']}</span>{d['where'] ? ` (${d['where']})` : ''}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Spinner({ label }: { label: string }): React.ReactElement {
  return (
    <div className="flex items-center gap-2 text-sm text-ink-muted py-6 justify-center" role="status">
      <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
      {label}
    </div>
  );
}

function ScoreRing({ score }: { score: number }): React.ReactElement {
  const tone = score >= 70 ? 'text-pass bg-pass-soft border-pass-soft' : score >= 40 ? 'text-warn bg-warn-soft border-warn-soft' : 'text-fail bg-fail-soft border-fail-soft';
  return (
    <div className={cn('flex flex-col items-center justify-center w-24 h-24 rounded-full border-4', tone)}>
      <span className="text-2xl font-semibold tabular-nums">{Math.round(score)}</span>
      <span className="text-[10px] uppercase tracking-wide">recoverable</span>
    </div>
  );
}

const CHANNEL_LABEL: Record<ContactPlanStep['channel'], string> = { phone: 'Phone', sms: 'SMS', email: 'Email', letter: 'Letter', in_person: 'In person' };

export function CollectionsPage(): React.ReactElement {
  const [minDpd, setMinDpd] = useState('10');
  const [maxDpd, setMaxDpd] = useState('60');
  const [loadingQueue, setLoadingQueue] = useState(false);
  const [queueError, setQueueError] = useState<ApiError | null>(null);
  const [queue, setQueue] = useState<QueueResponse | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('days_past_due');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const [selected, setSelected] = useState<Row | null>(null);
  const [staffContext, setStaffContext] = useState('');
  const [loadingPlan, setLoadingPlan] = useState(false);
  const [planError, setPlanError] = useState<ApiError | null>(null);
  const [planRetrying, setPlanRetrying] = useState(false);
  const [plan, setPlan] = useState<PlanResponse | null>(null);

  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<ApiError | null>(null);
  const [sentItemId, setSentItemId] = useState<string | null>(null);

  const [queueTotal, setQueueTotal] = useState<number | null>(null);
  const [queueRetrying, setQueueRetrying] = useState(false);
  const loadQueue = useCallback(async () => {
    setLoadingQueue(true); setQueueError(null); setQueueRetrying(false); setQueueTotal(null);
    try {
      const band = { minDpd: minDpd.trim(), maxDpd: maxDpd.trim() };
      const q = new URLSearchParams({ ...band, limit: String(QUEUE_PAGE) });
      const [rows, count] = await Promise.all([
        withOneRetryOnTimeout(() => api<QueueResponse>(`/api/core/sandbox/collections/queue?${q.toString()}`), () => setQueueRetrying(true)),
        api<{ count: number }>(`/api/core/sandbox/collections/queue/count?${new URLSearchParams(band).toString()}`).catch(() => null),
      ]);
      setQueue(rows);
      setQueueTotal(count?.count ?? null);
    } catch (err) {
      setQueueError(err instanceof ApiError ? err : new ApiError((err as Error).message));
    } finally {
      setLoadingQueue(false); setQueueRetrying(false);
    }
  }, [minDpd, maxDpd]);

  useEffect(() => { void loadQueue(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const sorted = useMemo(() => {
    const rows = [...(queue?.rows ?? [])];
    const num = (r: Row) => { const n = Number(r[sortKey] ?? ''); return Number.isNaN(n) ? -Infinity : n; };
    rows.sort((a, b) => {
      const va = sortKey === 'next_payment_date' ? (a[sortKey] ?? '') : num(a);
      const vb = sortKey === 'next_payment_date' ? (b[sortKey] ?? '') : num(b);
      const c = va < vb ? -1 : va > vb ? 1 : 0;
      return sortDir === 'asc' ? c : -c;
    });
    return rows;
  }, [queue, sortKey, sortDir]);

  function toggleSort(k: SortKey): void {
    if (k === sortKey) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortKey(k); setSortDir('desc'); }
  }

  const openRow = useCallback((r: Row) => {
    setSelected(r); setPlan(null); setPlanError(null); setSentItemId(null); setSendError(null); setStaffContext('');
  }, []);

  const generate = useCallback(async () => {
    if (!selected) return;
    setLoadingPlan(true); setPlanError(null); setPlan(null); setSentItemId(null); setSendError(null); setPlanRetrying(false);
    try {
      const body = staffContext.trim() ? { staffContext: staffContext.trim() } : {};
      const res = await withOneRetryOnTimeout(
        () => api<PlanResponse>(`/api/core/sandbox/collections/${encodeURIComponent(selected['account_number'] ?? '')}/outreach-plan`, { method: 'POST', body: JSON.stringify(body) }),
        () => setPlanRetrying(true),
      );
      setPlan(res);
    } catch (err) {
      setPlanError(err instanceof ApiError ? err : new ApiError((err as Error).message));
    } finally {
      setLoadingPlan(false); setPlanRetrying(false);
    }
  }, [selected, staffContext]);

  const sendToReview = useCallback(async () => {
    if (!plan) return;
    setSending(true); setSendError(null);
    try {
      const item = await api<{ itemId: string }>(`/api/core/sandbox/collections/${encodeURIComponent(plan.accountNumber)}/outreach-plan/queue`, {
        method: 'POST',
        body: JSON.stringify({ plan: plan.plan, data: plan.data }),
      });
      setSentItemId(item.itemId);
    } catch (err) {
      setSendError(err instanceof ApiError ? err : new ApiError((err as Error).message));
    } finally {
      setSending(false);
    }
  }, [plan]);

  const SortHeader = ({ k, label, right }: { k: SortKey; label: string; right?: boolean }): React.ReactElement => (
    <th className={cn('px-3 py-2 font-medium select-none cursor-pointer hover:text-ink', right ? 'text-right' : 'text-left')} onClick={() => toggleSort(k)} aria-sort={sortKey === k ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <span className="inline-flex items-center gap-1">
        {label}
        {sortKey === k && (sortDir === 'asc' ? <ArrowUp className="h-3 w-3" aria-hidden="true" /> : <ArrowDown className="h-3 w-3" aria-hidden="true" />)}
      </span>
    </th>
  );

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-ink flex items-center gap-2">
            <PhoneCall className="h-5 w-5 text-brand-dark" aria-hidden="true" />
            Collections
          </h1>
          <p className="text-sm text-ink-muted mt-1">
            Early-delinquency queue from the core (deterministic SQL). Generate an outreach plan per loan; every plan is schema-checked, grounded against the core data, and screened by the compliance lint before you can send it to compliance review. Nothing here contacts a member.
          </p>
        </div>
        <span className="inline-flex items-center gap-1 rounded-full bg-info-soft text-info text-xs px-2 py-0.5 font-medium whitespace-nowrap">
          <Database className="h-3 w-3" aria-hidden="true" /> Symitar sandbox · via Uptiq
        </span>
      </div>

      <form onSubmit={(e) => { e.preventDefault(); void loadQueue(); }} className="flex flex-wrap items-end gap-3 rounded-xl border border-line bg-white p-4">
        <label className="flex flex-col text-xs font-medium text-ink-soft gap-1">
          Min DPD
          <input value={minDpd} onChange={(e) => setMinDpd(e.target.value as string)} inputMode="numeric" className="px-3 py-2 text-sm border border-line-strong rounded-lg w-24 focus:outline-none focus:ring-2 focus:ring-info" />
        </label>
        <label className="flex flex-col text-xs font-medium text-ink-soft gap-1">
          Max DPD
          <input value={maxDpd} onChange={(e) => setMaxDpd(e.target.value as string)} inputMode="numeric" className="px-3 py-2 text-sm border border-line-strong rounded-lg w-24 focus:outline-none focus:ring-2 focus:ring-info" />
        </label>
        <Button size="sm" type="submit" disabled={loadingQueue}>
          {loadingQueue ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <RefreshCw className="h-4 w-4" aria-hidden="true" />}
          {loadingQueue ? (queueRetrying ? 'Agent timed out once — retrying…' : 'Querying core…') : 'Refresh queue'}
        </Button>
        {queue && !loadingQueue && (
          <span className="text-xs text-ink-muted ml-auto">{queue.rows.length}{queueTotal !== null && queueTotal > queue.rows.length ? ` of ${queueTotal}` : ''} loan{(queueTotal ?? queue.rows.length) === 1 ? '' : 's'} · {queue.minDpd}–{queue.maxDpd} DPD · {queue.meta.cached ? 'cached' : `${(queue.meta.latencyMs / 1000).toFixed(1)}s`}{queueTotal !== null && queueTotal > queue.rows.length ? ' · highest DPD and balance first' : ''}</span>
        )}
      </form>

      {queueError && <ErrorBox error={queueError} />}
      {loadingQueue && <Spinner label="Pulling the delinquency book from the core…" />}

      {queue && !loadingQueue && (
        <div className="overflow-x-auto rounded-lg border border-line">
          <table className="min-w-full text-sm">
            <thead className="bg-bg text-xs uppercase tracking-wide text-ink-muted">
              <tr>
                <th className="px-3 py-2 font-medium text-left">Account #</th>
                <th className="px-3 py-2 font-medium text-left">Member</th>
                <th className="px-3 py-2 font-medium text-left">Contact pref.</th>
                <th className="px-3 py-2 font-medium text-left">Loan</th>
                <SortHeader k="current_principal" label="Balance" right />
                <SortHeader k="days_past_due" label="DPD" right />
                <SortHeader k="amount_past_due" label="Past due" right />
                <SortHeader k="next_payment_date" label="Next payment" />
                <th className="px-3 py-2 font-medium text-left">Last payment</th>
                <th className="px-3 py-2 font-medium text-left">Case</th>
                <th className="px-3 py-2 font-medium text-left">Last activity</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-sunken bg-white">
              {sorted.length === 0 ? (
                <tr><td colSpan={12} className="px-3 py-6 text-center text-ink-muted">No loans in this DPD band.</td></tr>
              ) : sorted.map((r) => (
                <tr key={r['account_number']} className={cn('hover:bg-bg cursor-pointer', selected?.['account_number'] === r['account_number'] && 'bg-info-soft')} onClick={() => openRow(r)}>
                  <td className="px-3 py-2 font-mono text-xs text-ink-soft">{r['account_number']}</td>
                  <td className="px-3 py-2 text-ink">{text(r['first_name'])} {text(r['last_name'])} <span className="text-ink-muted">#{r['member_number']}</span></td>
                  <td className="px-3 py-2 text-ink-soft">{text(r['preferred_contact_method'])}</td>
                  <td className="px-3 py-2 text-ink-soft">{text(r['loan_type'])}{r['collateral_type'] ? ` · ${r['collateral_type']}` : ''}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-ink-soft">{money(r['current_principal'])}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-medium text-ink">{text(r['days_past_due'])}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-ink-soft">{money(r['amount_past_due'])}</td>
                  <td className="px-3 py-2 text-ink-soft whitespace-nowrap">{date(r['next_payment_date'])} · {money(r['next_payment_amount'])}</td>
                  <td className="px-3 py-2 text-ink-soft whitespace-nowrap">{r['last_payment_date'] ? date(r['last_payment_date']) : r['est_last_payment_date'] ? <span title="Derived: next payment date − 1 month">~{date(r['est_last_payment_date'])}</span> : '—'}</td>
                  <td className="px-3 py-2">{r['open_case_count'] && r['open_case_count'] !== '0' ? <span className="rounded-full bg-warn-soft text-warn text-xs px-2 py-0.5">{r['open_case_status']}</span> : <span className="text-ink-muted text-xs">none</span>}</td>
                  <td className="px-3 py-2 text-ink-soft whitespace-nowrap">{r['last_activity_date'] ? `${date(r['last_activity_date'])} · ${r['last_activity_outcome']}` : '—'}</td>
                  <td className="px-3 py-2 text-xs text-brand-dark whitespace-nowrap">Plan →</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ---- Plan drawer ---- */}
      {selected && (
        <div className="fixed inset-0 z-40 flex justify-end" role="dialog" aria-modal="true" aria-label={`Outreach plan for account ${selected['account_number']}`}>
          <div className="absolute inset-0 bg-black/30" onClick={() => setSelected(null)} />
          <aside className="relative w-full max-w-2xl h-full bg-white shadow-xl overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-line px-6 py-4 flex items-start justify-between gap-4 z-10">
              <div>
                <h2 className="text-lg font-semibold text-ink">{text(selected['first_name'])} {text(selected['last_name'])} <span className="text-sm font-normal text-ink-muted">#{selected['member_number']}</span></h2>
                <p className="text-sm text-ink-muted">Account {selected['account_number']} · {text(selected['loan_type'])} · {money(selected['current_principal'])} · <span className="font-medium text-ink-soft">{selected['days_past_due']} DPD</span> · prefers {text(selected['preferred_contact_method'])}</p>
              </div>
              <button onClick={() => setSelected(null)} className="text-ink-muted hover:text-ink-soft" aria-label="Close"><X className="h-5 w-5" /></button>
            </div>

            <div className="px-6 py-5 space-y-5">
              {!plan && !loadingPlan && (
                <div className="rounded-xl border border-line p-4 space-y-3">
                  <label className="flex flex-col text-xs font-medium text-ink-soft gap-1">
                    Context for the agent (optional, e.g. "member called on 9/8 and mentioned a job change")
                    <textarea value={staffContext} onChange={(e) => setStaffContext(e.target.value as string)} maxLength={2000} rows={3} className="px-3 py-2 text-sm border border-line-strong rounded-lg focus:outline-none focus:ring-2 focus:ring-info" />
                  </label>
                  <Button size="sm" onClick={() => { void generate(); }}>
                    <PhoneCall className="h-4 w-4" aria-hidden="true" /> Generate outreach plan
                  </Button>
                  <p className="text-xs text-ink-muted">Pulls loan, member, contacts, 90 days of activity and collections history from the core (8 queries in parallel), then asks the Collections agent. Expect 20–60 seconds.</p>
                </div>
              )}

              {loadingPlan && <Spinner label={planRetrying ? 'The agent timed out once — retrying (evidence is cached, so only the agent call repeats)…' : 'Gathering evidence from the core and asking the Collections agent… this takes a while.'} />}
              {planError && (
                <div className="space-y-3">
                  <ErrorBox error={planError} />
                  <button onClick={() => { void generate(); }} className="text-sm text-brand-dark hover:underline">Try again</button>
                </div>
              )}

              {plan && (
                <>
                  <section className="flex items-center gap-5 rounded-xl border border-line p-4">
                    <ScoreRing score={plan.plan.recoverability_score} />
                    <div className="flex-1">
                      <h3 className="text-sm font-semibold text-ink mb-1">Drivers</h3>
                      <ul className="list-disc ml-5 text-sm text-ink-soft space-y-0.5">{plan.plan.drivers.map((d, i) => <li key={i}>{d}</li>)}</ul>
                      <p className="mt-2 text-xs text-ink-muted flex items-center gap-1">
                        <CheckCircle2 className="h-3.5 w-3.5 text-pass" aria-hidden="true" /> {plan.grounding.checked} figure{plan.grounding.checked === 1 ? '' : 's'} grounded against core data · {plan.lint.rulesEvaluated} compliance rules passed · agent {(plan.agent.latencyMs / 1000).toFixed(1)}s
                      </p>
                    </div>
                  </section>

                  {plan.plan.contact_plan.length === 0 && (
                    <section role="status" className="rounded-xl border border-warn-fill bg-warn-soft p-4">
                      <h3 className="text-sm font-semibold text-warn flex items-center gap-1"><ShieldCheck className="h-4 w-4" aria-hidden="true" /> Compliance hold — no outreach recommended</h3>
                      <p className="text-sm text-warn mt-1">The agent found a stop signal in the core data (bankruptcy, deceased, dispute, active duty, cease-contact or an existing workout plan) and drafted no message. Send the hold to compliance review so the case is routed by a person.</p>
                    </section>
                  )}

                  <section>
                    <h3 className="text-sm font-semibold text-ink mb-2">Contact plan</h3>
                    <ol className="relative border-l border-line ml-2 space-y-3">
                      {plan.plan.contact_plan.length === 0 && <li className="ml-4 text-sm text-ink-muted italic">No contact steps.</li>}
                      {plan.plan.contact_plan.map((s, i) => (
                        <li key={i} className="ml-4">
                          <span className="absolute -left-1.5 mt-1.5 h-3 w-3 rounded-full bg-brand" />
                          <div className="text-sm"><span className="font-medium text-ink">{CHANNEL_LABEL[s.channel]}</span> <span className="text-ink-muted">· {s.when}</span></div>
                          <div className="text-sm text-ink-soft">{s.purpose}</div>
                        </li>
                      ))}
                    </ol>
                  </section>

                  <section>
                    <h3 className="text-sm font-semibold text-ink mb-2">First message draft <span className="text-xs font-normal text-ink-muted">(read-only — compliance approves before anything is sent)</span></h3>
                    {plan.plan.first_message_draft ? (
                      <pre className="whitespace-pre-wrap font-sans text-sm text-ink rounded-lg border border-line bg-bg p-4">{plan.plan.first_message_draft}</pre>
                    ) : (
                      <p className="text-sm text-ink-muted italic rounded-lg border border-dashed border-line-strong p-4">No draft — compliance hold.</p>
                    )}
                  </section>

                  <section className="grid md:grid-cols-2 gap-4">
                    <div>
                      <h3 className="text-sm font-semibold text-ink mb-1 flex items-center gap-1"><ShieldCheck className="h-4 w-4 text-brand-dark" aria-hidden="true" /> Compliance notes</h3>
                      <ul className="list-disc ml-5 text-sm text-ink-soft space-y-0.5">{plan.plan.compliance_notes.length === 0 ? <li className="text-ink-muted list-none">None</li> : plan.plan.compliance_notes.map((n, i) => <li key={i}>{n}</li>)}</ul>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-ink mb-1">Do not</h3>
                      <ul className="list-disc ml-5 text-sm text-ink-soft space-y-0.5">{plan.plan.do_not.length === 0 ? <li className="text-ink-muted list-none">None</li> : plan.plan.do_not.map((n, i) => <li key={i}>{n}</li>)}</ul>
                    </div>
                  </section>

                  <section className="rounded-xl border border-line p-4 flex flex-wrap items-center gap-3">
                    {sentItemId ? (
                      <span className="inline-flex items-center gap-2 text-sm text-pass"><CheckCircle2 className="h-4 w-4" aria-hidden="true" /> Sent to compliance review · item {sentItemId.slice(0, 8)}…</span>
                    ) : (
                      <Button size="sm" onClick={() => { void sendToReview(); }} disabled={sending}>
                        {sending ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <ShieldCheck className="h-4 w-4" aria-hidden="true" />}
                        {sending ? 'Sending…' : 'Send to compliance review'}
                      </Button>
                    )}
                    <button onClick={() => { void generate(); }} disabled={loadingPlan || sending} className="text-sm text-ink-soft hover:underline">Regenerate</button>
                    <span className="text-xs text-ink-muted ml-auto">Lands in the Review Queue for a compliance officer. No send path exists.</span>
                  </section>
                  {sendError && <ErrorBox error={sendError} />}
                </>
              )}
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}
